'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { OPEN_HOUR, CLOSE_HOUR } from '@/lib/business-hours';
import './footer.css';

interface FooterProps {
  className?: string;
}

const mainLinks = [
  { label: 'Меню', href: '/#menu' },
  { label: 'Акции', href: '/actions' },
  { label: 'Доставка', href: '/delivery' },
  { label: 'Оплата', href: '/payment' },
  { label: 'О нас', href: '/about' },
  { label: 'Вопросы и ответы', href: '/faq' },
];
const companyLinks = [
  { label: 'Отзывы', href: '/reviews' },
  { label: 'Блог', href: '/blog' },
  { label: 'Вакансии', href: '/vacancies' },
  { label: 'Арендодателям', href: '/rent' },
  { label: 'Аллергены', href: '/allergens' },
];
const mobileExtraLinks = [
  ...mainLinks.filter((link) => link.href === '/about' || link.href === '/payment'),
  ...companyLinks,
];

const legalLinks = [
  { label: 'Политика конфиденциальности', href: '/privacy' },
  { label: 'Публичная оферта', href: '/oferta' },
  { label: 'Безопасные платежи', href: '/secure-payments' },
  { label: 'Политика возврата', href: '/refund' },
];

const VKIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className || 'w-6 h-6'}
    style={style}
  >
    <path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14c5.6 0 6.93-1.33 6.93-6.93V8.93C22 3.33 20.67 2 15.07 2m3.08 14.27h-1.46c-.55 0-.72-.44-1.71-1.45-1-.97-1.43-1.1-1.68-1.1-.35 0-.45.1-.45.58v1.31c0 .41-.13.66-1.21.66-1.79 0-3.78-.97-5.18-2.77-2.1-2.95-2.67-5.16-2.67-5.61 0-.25.1-.48.58-.48h1.46c.44 0 .6.19.77.65.85 2.45 2.27 4.6 2.85 4.6.22 0 .32-.1.32-.66V9.13c-.07-1.14-.67-1.24-.67-1.65 0-.2.17-.41.44-.41h2.3c.37 0 .5.19.5.63v3.41c0 .37.17.5.27.5.22 0 .4-.13.81-.54 1.25-1.4 2.14-3.56 2.14-3.56.12-.25.32-.48.77-.48h1.46c.44 0 .54.23.44.63-.19.87-2.03 3.47-2.03 3.47-.16.26-.22.38 0 .67.16.22.69.67 1.04 1.08.65.72 1.15 1.33 1.28 1.75.14.41-.09.62-.52.62" />
  </svg>
);

const MaxIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="-100 -100 960 958"
    fill="currentColor"
    className={className}
    style={style}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M392.07 186.804C292.648 181.591 215.063 250.579 197.954 358.557C183.786 447.977 208.906 556.935 230.392 562.419C239.505 564.745 261.436 547.971 277.392 532.925C280.391 530.096 284.927 529.62 288.445 531.771C313.317 546.98 341.477 558.41 372.522 560.038C474.59 565.39 565.042 485.507 570.388 383.43C575.735 281.353 494.139 192.157 392.07 186.804ZM225.581 705.191C221.732 702.466 216.428 703.205 213.202 706.646C170.082 752.636 59.7277 784.896 54.6727 722.126C54.6727 672.946 43.6266 631.503 31.4646 585.873C16.5692 529.989 0 467.824 0 377.475C0 162.027 176.694 0 386.194 0C595.694 0 759.951 169.938 759.951 379.567C759.951 589.196 590.466 754.95 388.176 754.95C316.409 754.95 281.583 744.841 225.581 705.191Z"
    />
  </svg>
);

export function Footer({ className }: FooterProps) {
  const workingHours = `${OPEN_HOUR}:00–${CLOSE_HOUR}:00`;

  return (
    <footer className={cn('azia-footer', className)} aria-label="Подвал сайта">
      <div className="azia-footer-inner">
        <div className="azia-footer-top">
          <div className="azia-footer-identity">
            <Link href="/" className="azia-footer-brand" aria-label="Азиаточка — на главную">
              <span className="azia-footer-wordmark" aria-hidden="true" />
            </Link>
            <span className="azia-footer-city">Ростов-на-Дону</span>
          </div>
          <div className="azia-footer-contact">
            <a className="azia-footer-phone" href="tel:88633335050">
              <span>8 (863) 333-50-50</span>
              <span className="azia-footer-call-arrow" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 18 18 6M6 6h12v12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </a>
            <p>
              Заказы ежедневно, <span>{workingHours}</span>
            </p>
          </div>
          <div className="azia-footer-socials" aria-label="Социальные сети и поддержка">
            <a
              href="https://vk.com/aziatochka_ru"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Азиаточка во ВКонтакте — откроется в новой вкладке"
              title="Азиаточка во ВКонтакте"
            >
              <VKIcon className="azia-footer-social-icon" />
              <span>VK</span>
            </a>
            <a
              href="https://max.ru/u/f9LHodD0cOJy8oPBxUr-x-mis-JgEXUn4KSVirJM9Ix_OdGwXP4A3anpCCU"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Написать в MAX — откроется в новой вкладке"
              title="Написать в MAX"
            >
              <MaxIcon className="azia-footer-social-icon" />
              <span>MAX</span>
            </a>
          </div>
        </div>
        <nav className="azia-footer-main-nav" aria-label="Основные разделы">
          {mainLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              data-mobile-hidden={
                link.href === '/about' || link.href === '/payment' ? true : undefined
              }
            >
              {link.href === '/faq' ? (
                <>
                  <span className="azia-footer-desktop-label">{link.label}</span>
                  <span className="azia-footer-mobile-label">Помощь</span>
                </>
              ) : (
                link.label
              )}
            </Link>
          ))}
        </nav>
        <nav className="azia-footer-secondary-nav" aria-label="Информация о компании">
          {companyLinks.map((link) => (
            <Link key={link.href} href={link.href}>
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="azia-footer-mobile-folds">
          <details className="azia-footer-fold">
            <summary>
              Компания и сервис <span className="azia-footer-fold-icon" aria-hidden="true" />
            </summary>
            <nav aria-label="Компания и сервис" className="azia-footer-extra-links">
              {mobileExtraLinks.map((link) => (
                <Link key={link.href} href={link.href}>
                  {link.label}
                </Link>
              ))}
            </nav>
          </details>
          <details className="azia-footer-fold">
            <summary>
              Документы <span className="azia-footer-fold-icon" aria-hidden="true" />
            </summary>
            <nav aria-label="Документы" className="azia-footer-mobile-legal">
              {legalLinks.map((link) => (
                <Link key={link.href} href={link.href}>
                  {link.label}
                </Link>
              ))}
            </nav>
          </details>
        </div>
        <div className="azia-footer-bottom">
          <nav className="azia-footer-legal" aria-label="Правовая информация">
            {legalLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="azia-footer-copyright">
            <span>
              © Азиаточка <span suppressHydrationWarning>{new Date().getFullYear()}</span>
            </span>
            <span>Все права защищены</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
